import { InfoOutlineIcon } from '@chakra-ui/icons'
import React, { useState, useEffect, useRef } from 'react'
import { Box, Flex, SkeletonText, Text, Tooltip, InputGroup, Input, List, ListItem } from '@chakra-ui/react'
import { useGetAppEventsQuery } from 'store/campaigns/adsetFormApi';

function AppEventsUpdate({ adsAccountId, preFilledData = {}, onIncludeAppEventChange }) {
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  const [showPopup, setShowPopup] = useState(false);
  const [appEventOptions, setAppEventOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAppOptions, setSelectedAppOptions] = useState({});

  // read current app_id / initial type from prefilled
  const app_id = preFilledData?.promoted_object?.application_id ?? null;
  const initialType = preFilledData?.promoted_object?.custom_event_type ?? '';

  // fetch events
  const { data: appEvents, isLoading: isAppEventLoading, isFetching } =
    useGetAppEventsQuery({ adsAccountId, app_id }, { skip: !app_id });

  // track changes to app_id and initialType to reset & (re)sync exactly once
  const prevAppIdRef = useRef(app_id);
  const prevTypeRef = useRef(initialType);
  const initialSyncRef = useRef(false);

  // reset guards when app_id or initialType actually changes
  useEffect(() => {
    if (prevAppIdRef.current !== app_id) {
      prevAppIdRef.current = app_id;
      initialSyncRef.current = false;
      setSelectedAppOptions({});
      setSearchTerm('');
      setShowPopup(false);
      setAppEventOptions([]);
      onIncludeAppEventChange?.({});
    }
  }, [app_id, onIncludeAppEventChange]);

  useEffect(() => {
    if (prevTypeRef.current !== initialType) {
      prevTypeRef.current = initialType;
      initialSyncRef.current = false;
      // don't clear selection here; we'll re-sync below when events are available
    }
  }, [initialType]);

  // Always keep list = ALL events unless searching
  useEffect(() => {
    if (!appEvents) return;
    if (searchTerm?.length > 0) {
      const filtered = appEvents.filter((app) =>
        app?.lable?.toLowerCase?.().includes(searchTerm.toLowerCase())
      );
      setAppEventOptions(filtered || []);
      setShowPopup((filtered?.length ?? 0) > 0);
    } else {
      setAppEventOptions(appEvents || []);
      setShowPopup(false); // not searching -> keep list closed by default
    }
  }, [appEvents, searchTerm]);

  // One-time initial sync to parent when preFilledData present
  useEffect(() => {
    if (!appEvents) return;
    if (initialSyncRef.current) return;

    const typeLc = initialType?.toLowerCase?.() || '';
    if (typeLc) {
      const match =
        appEvents.find(
          (app) =>
            app?.value?.toLowerCase?.() === typeLc ||
            app?.value?.toLowerCase?.().includes(typeLc)
        ) || null;

      if (match) {
        setSelectedAppOptions(match);
        initialSyncRef.current = true;
        setTimeout(() => onIncludeAppEventChange?.(match), 0);
      } else {
        // no match -> clear once
        setSelectedAppOptions({});
        initialSyncRef.current = true;
        setTimeout(() => onIncludeAppEventChange?.({}), 0);
      }
    } else {
      // no initial type -> clear once
      setSelectedAppOptions({});
      initialSyncRef.current = true;
      setTimeout(() => onIncludeAppEventChange?.({}), 0);
    }
  }, [appEvents, initialType, onIncludeAppEventChange]);

  const handleAppEventSelect = (app) => {
    const picked = app || {};
    setSelectedAppOptions(picked);
    setTimeout(() => onIncludeAppEventChange?.(picked), 0);
    setSearchTerm('');
    setShowPopup(false);
    setAppEventOptions(appEvents || []);
  };

  // Close popup on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setShowPopup(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inputValue = selectedAppOptions?.lable ?? searchTerm;

  return (
    <Flex direction="column" gap={2} w="100%">
      <Flex alignItems="center">
        <Text fontWeight="bold">App event</Text>
        <Tooltip
          borderRadius={5}
          label={
            <Flex fontSize="14px" p="5px" direction="column" gap={2}>
              <Text fontWeight="bold">About app events</Text>
              <Text fontSize="12px">
                App events are the actions people take while using your app, such as making a purchase or achieving a new level in a game.
              </Text>
            </Flex>
          }
          fontSize="md"
        >
          <InfoOutlineIcon pointerEvents="all" ml={2} />
        </Tooltip>
      </Flex>

      {isAppEventLoading || isFetching ? (
        <Flex>
          <SkeletonText width="80%" noOfLines={2} skeletonHeight={2} />
        </Flex>
      ) : (
        <Box position="relative">
          <Flex>
            <InputGroup>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Choose or Search for an app event"
                borderRadius="md"
                size="sm"
                // keep read-only update flow; change to a condition if you want it editable
                isDisabled={!app_id}
                onFocus={() => {
                  if (appEvents?.length > 0 && searchTerm.length > 0) {
                    setShowPopup(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Backspace' &&
                    selectedAppOptions?.lable &&
                    searchTerm === ''
                  ) {
                    handleAppEventSelect({});
                    if (appEvents?.length > 0) {
                      setAppEventOptions(appEvents);
                      setShowPopup(true);
                    }
                  }
                }}
              />
            </InputGroup>
          </Flex>

          <Flex mt={2}>
            {showPopup && (appEventOptions?.length ?? 0) > 0 && (
              <List
                ref={popupRef}
                position="absolute"
                top="100%"
                width="100%"
                border="1px solid #ccc"
                backgroundColor="white"
                zIndex="1000"
                maxHeight="220px"
                overflowY="auto"
                borderRadius="md"
              >
                {appEventOptions.map((app, index) => (
                  <ListItem
                    key={`${app?.value ?? 'val'}-${index}`}
                    p="6px"
                    px="10px"
                    cursor="pointer"
                    _hover={{ backgroundColor: '#f0f0f0' }}
                    onClick={() => handleAppEventSelect(app)}
                  >
                    <Flex gap={2} align="center">
                      <Text fontSize="14px">{app?.lable}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            )}
          </Flex>
        </Box>
      )}
    </Flex>
  );
}

export default AppEventsUpdate;
